import React from 'react';
import { Link } from 'react-router-dom';

import { deleteJSON, getJSON, patchJSON, postJSON } from '../../api/client.js';
import {
  UniActionButton, UniPageShell, UniSection, UniStatusPill, UniTable,
} from '../waba/WabaShared.jsx';
import { SB_API, THEME, HOME } from './SignalBridgeShared.jsx';

const P = { theme: THEME, api: SB_API, home: HOME };

function prettyJson(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch (err) {
    return String(value);
  }
}

function templateBodyText(body) {
  if (typeof body === 'string') {
    try {
      return JSON.stringify(JSON.parse(body), null, 2);
    } catch (err) {
      return body;
    }
  }
  return prettyJson(body && typeof body === 'object' ? body : {});
}

function localJsonProblem(text) {
  const raw = String(text || '').trim();
  if (!raw) return 'JSON template is empty.';
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return 'JSON template must be an object with a data array of server events.';
    }
    return '';
  } catch (err) {
    return `JSON is not valid: ${err.message}`;
  }
}

function fieldError(err) {
  const data = err && err.data;
  if (!data) return err && err.message ? String(err.message) : 'Something went wrong.';
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail)) return data.detail.map(String).join(' ');
  if (data.body) return Array.isArray(data.body) ? data.body.join(' ') : String(data.body);
  try {
    return JSON.stringify(data);
  } catch (e) {
    return String(err.message || err);
  }
}

function kindLabel(row) {
  return row.is_system ? 'Meta template' : 'Custom';
}

function sortTemplates(rows) {
  return [...(rows || [])].sort((a, b) => {
    if (Boolean(a.is_system) !== Boolean(b.is_system)) return a.is_system ? -1 : 1;
    if (Boolean(a.is_active) !== Boolean(b.is_active)) return a.is_active ? -1 : 1;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

function formatWhen(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  } catch (err) {
    return String(value);
  }
}

function TemplateCheck({ check, busy }) {
  const warnings = (check && check.warnings) || [];
  const failed = check && check.ok === false;
  const tone = busy || !check
    ? ' sb-tpl-check--pending'
    : failed
      ? ' sb-tpl-check--bad'
      : warnings.length
        ? ' sb-tpl-check--warn'
        : '';
  let title = 'Checking JSON template…';
  if (check && !busy) {
    title = failed
      ? 'JSON template needs a fix'
      : warnings.length
        ? 'JSON template works, with notes'
        : 'JSON template works';
  }
  return (
    <div className={`sb-tpl-check${tone}`} aria-live="polite">
      <h3>{title}</h3>
      <p>
        {check && !busy
          ? check.summary
          : 'Parsing the JSON and rendering a sample purchase the way Meta receives it.'}
      </p>
      {check && Array.isArray(check.checks) && check.checks.length ? (
        <ul>
          {check.checks.map((row) => (
            <li key={row.id}>
              {row.ok ? '✓' : '✕'} {row.label}: {row.detail}
            </li>
          ))}
        </ul>
      ) : null}
      {check && !busy && warnings.length ? (
        <ul>
          {warnings.map((item) => <li key={item}>Note: {item}</li>)}
        </ul>
      ) : null}
      {check && check.event ? (
        <details>
          <summary>Rendered sample Meta receives</summary>
          <pre className="uni-json">{prettyJson(check.event)}</pre>
        </details>
      ) : null}
    </div>
  );
}

export function SignalBridgeTemplates() {
  const [templates, setTemplates] = React.useState([]);
  const [catalog, setCatalog] = React.useState(null);
  const [selectedId, setSelectedId] = React.useState('');
  const [view, setView] = React.useState('list');
  const [tab, setTab] = React.useState('meta');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [bodyText, setBodyText] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [sourceId, setSourceId] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [testFields, setTestFields] = React.useState({});
  const [preview, setPreview] = React.useState(null);
  const [previewBusy, setPreviewBusy] = React.useState(false);
  const [pixels, setPixels] = React.useState([]);
  const [incoming, setIncoming] = React.useState([]);
  const [pixel, setPixel] = React.useState('');
  const [incomingId, setIncomingId] = React.useState('');
  const [testCode, setTestCode] = React.useState('');
  const [sendOut, setSendOut] = React.useState(null);
  const [sendBusy, setSendBusy] = React.useState(false);
  const [editorFor, setEditorFor] = React.useState('');
  const [check, setCheck] = React.useState(null);
  const [checkBusy, setCheckBusy] = React.useState(false);
  const bodyRef = React.useRef(null);

  const ordered = sortTemplates(templates);
  const selected = (templates || []).find((row) => String(row.id) === String(selectedId)) || null;
  const defaultRow = (templates || []).find((row) => row.is_active) || null;
  const metaSource = (templates || []).find((row) => row.is_system) || ordered[0] || null;
  const jsonProblem = (
    selected && !selected.is_system && editorFor === String(selected.id)
  ) ? localJsonProblem(bodyText) : '';

  const load = React.useCallback(async (keepId) => {
    setLoading(true);
    setError('');
    try {
      const [list, cat] = await Promise.all([
        getJSON(`${SB_API}/payload-templates/`),
        getJSON(`${SB_API}/payload-templates/catalog/`),
      ]);
      let pix = { results: [] };
      let inc = { results: [] };
      try { pix = await getJSON(`${SB_API}/pixels/?page_size=50`); } catch (e) { /* optional */ }
      try { inc = await getJSON(`${SB_API}/incoming/?page_size=20`); } catch (e) { /* optional */ }
      const rows = list.results || list || [];
      setTemplates(rows);
      setCatalog(cat);
      setPixels(pix.results || pix || []);
      setIncoming(inc.results || inc || []);
      const meta = rows.find((r) => r.is_system);
      setSourceId((prev) => prev || (meta && meta.id) || (rows[0] && rows[0].id) || '');
      if (keepId) setSelectedId(String(keepId));
    } catch (err) {
      setError(fieldError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    if (!selected) {
      setName('');
      setDescription('');
      setBodyText('');
      return;
    }
    setName(selected.name || '');
    setDescription(selected.description || '');
    setBodyText(templateBodyText(selected.body));
    setEditorFor(String(selected.id));
  }, [selectedId, selected && selected.updated_at]);

  React.useEffect(() => {
    const sample = (catalog && catalog.sample_context) || {};
    const tokens = (selected && selected.placeholders) || Object.keys(sample);
    const next = {};
    tokens.forEach((key) => {
      const short = String(key).split('.').pop();
      if (sample[short] != null) next[short] = sample[short];
      else if (sample[key] != null) next[key] = sample[key];
      else next[short] = '';
    });
    ['event_name', 'event_id', 'event_time', 'fbc', 'fbp', 'external_id', 'em', 'value', 'currency'].forEach((key) => {
      if (next[key] == null && sample[key] != null) next[key] = sample[key];
    });
    setTestFields(next);
    setPreview(null);
    setSendOut(null);
  }, [selectedId, catalog, selected && selected.placeholders && selected.placeholders.join(',')]);

  const openEditor = (row, nextTab) => {
    setSelectedId(String(row.id));
    setCheck(null);
    setTab(nextTab || (row.is_system ? 'meta' : 'params'));
    setView('edit');
    setNotice('');
    setError('');
  };

  const backToList = () => {
    setView('list');
    setCreating(false);
    setError('');
  };

  const insertToken = (token) => {
    const el = bodyRef.current;
    const snippet = `{{${token}}}`;
    if (!el) {
      setBodyText((text) => `${text}${snippet}`);
      setEditorFor(String(selectedId));
      return;
    }
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const next = bodyText.slice(0, start) + snippet + bodyText.slice(end);
    setBodyText(next);
    setEditorFor(String(selectedId));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    });
  };

  React.useEffect(() => {
    if (view !== 'edit' || !selected || editorFor !== String(selected.id)) {
      return undefined;
    }
    let cancelled = false;
    const savedText = templateBodyText(selected.body);
    const delay = !selected.is_system && bodyText !== savedText ? 400 : 0;
    const timer = setTimeout(async () => {
      const problem = selected.is_system ? '' : localJsonProblem(bodyText);
      if (problem) {
        if (!cancelled) {
          setCheck({
            ok: false,
            summary: problem,
            errors: [problem],
            warnings: [],
            checks: [{ id: 'json', label: 'Valid JSON', ok: false, detail: problem }],
            event: null,
          });
          setCheckBusy(false);
        }
        return;
      }
      let payloadBody = selected.body;
      if (!selected.is_system) {
        try {
          payloadBody = JSON.parse(bodyText);
        } catch (err) {
          return;
        }
      }
      setCheckBusy(true);
      try {
        const data = await postJSON(`${SB_API}/payload-templates/${selected.id}/check/`, {
          body: payloadBody,
        });
        if (!cancelled) setCheck(data);
      } catch (err) {
        if (!cancelled) {
          const message = fieldError(err);
          setCheck({
            ok: false,
            summary: message,
            errors: [message],
            warnings: [],
            checks: [],
            event: null,
          });
        }
      } finally {
        if (!cancelled) setCheckBusy(false);
      }
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [view, selectedId, editorFor, bodyText, selected && selected.updated_at, selected && selected.is_system]);

  const save = async () => {
    if (!selected || selected.is_system) return;
    setSaving(true);
    setError('');
    setNotice('');
    let parsed;
    try {
      parsed = JSON.parse(bodyText);
    } catch (err) {
      setSaving(false);
      setError(`JSON is not valid: ${err.message}`);
      return;
    }
    if (check && check.ok === false) {
      setSaving(false);
      setError((check.errors && check.errors[0]) || check.summary || 'JSON template check failed.');
      return;
    }
    try {
      const saved = await patchJSON(`${SB_API}/payload-templates/${selected.id}/`, {
        name, description, body: parsed,
      });
      setNotice('Template saved.');
      await load(saved.id);
    } catch (err) {
      setError(fieldError(err));
    } finally {
      setSaving(false);
    }
  };

  const activate = async (row) => {
    setError('');
    setNotice('');
    try {
      await postJSON(`${SB_API}/payload-templates/${row.id}/activate/`);
      setNotice(`“${row.name}” is now the default payload sent to Meta.`);
      await load(row.id);
    } catch (err) {
      setError(fieldError(err));
    }
  };

  const duplicate = async (row, customName, { open = true } = {}) => {
    setError('');
    setNotice('');
    try {
      const created = await postJSON(`${SB_API}/payload-templates/${row.id}/duplicate/`, {
        name: customName || `Copy of ${row.name}`,
      });
      setNewName('');
      setCreating(false);
      setNotice('New template created from a copy. Edit the JSON, then Set as default when Meta should use it.');
      await load(created.id);
      if (open) openEditor(created, 'params');
      return created;
    } catch (err) {
      setError(fieldError(err));
      return null;
    }
  };

  const createNew = async () => {
    const source = (templates || []).find((row) => String(row.id) === String(sourceId))
      || metaSource
      || templates[0];
    if (!source) {
      setError('The Meta template has not loaded yet. Refresh and try again.');
      return;
    }
    const label = newName.trim() || `Copy of ${source.name}`;
    await duplicate(source, label);
  };

  const remove = async (row) => {
    if (row.is_system) return;
    if (!window.confirm(`Delete “${row.name}”? If it is the default, Meta’s template becomes the default.`)) return;
    setError('');
    try {
      await deleteJSON(`${SB_API}/payload-templates/${row.id}/`);
      setNotice(`Deleted “${row.name}”.`);
      setView('list');
      await load();
    } catch (err) {
      setError(fieldError(err));
    }
  };

  const runPreview = async () => {
    if (!selected) return;
    setPreviewBusy(true);
    setError('');
    try {
      const data = await postJSON(`${SB_API}/payload-templates/${selected.id}/test/`, {
        context: testFields,
        pixel: pixel || undefined,
        incoming_event: incomingId || undefined,
      });
      setPreview(data);
    } catch (err) {
      setError(fieldError(err));
    } finally {
      setPreviewBusy(false);
    }
  };

  const sendTest = async () => {
    if (!selected) return;
    setSendBusy(true);
    setSendOut(null);
    setError('');
    try {
      const data = await postJSON(`${SB_API}/payload-templates/${selected.id}/send-test/`, {
        pixel_account: pixel,
        test_event_code: testCode,
        context: testFields,
      });
      setSendOut(data);
      if (data.event) setPreview({ event: data.event, envelope: data.envelope });
    } catch (err) {
      setError(fieldError(err));
      setSendOut({ ok: false, hint: fieldError(err) });
    } finally {
      setSendBusy(false);
    }
  };

  const families = (catalog && catalog.families) || [];
  const placeholders = (catalog && catalog.placeholders) || [];

  return (
    <UniPageShell
      {...P}
      icon="⬡"
      title="Payload templates"
      subtitle="The JSON Meta CAPI receives. Meta’s template is the default — change the default, or create your own. Keep fbc (ad click) plus fbp and external_id (the purchaser)."
      helpPage="templates"
      helpApi={SB_API}
      helpOpen={false}
      actions={(
        <>
          {view === 'edit' ? (
            <UniActionButton hero onClick={backToList}>← All templates</UniActionButton>
          ) : (
            <UniActionButton hero primary onClick={() => { setCreating(true); setNotice(''); }}>
              + New template
            </UniActionButton>
          )}
          <UniActionButton hero to="/signalbridge/payloads">Meta payloads →</UniActionButton>
          <UniActionButton hero to={HOME}>← Home</UniActionButton>
        </>
      )}
    >
      {error ? <p className="sb-connect-error">{error}</p> : null}
      {notice ? <p className="ty-ok" style={{ margin: '8px 0' }}>{notice}</p> : null}

      {view === 'list' && (
        <>
          <div className="sb-tpl-howto">
            <h3>How to create a new template</h3>
            <ol>
              <li>Click <strong>New template</strong>.</li>
              <li>Give it a name. Leave “Start from” on <strong>Meta CAPI default</strong> unless you want to copy one of yours.</li>
              <li>Click <strong>Create template</strong>. That opens a copy you can edit.</li>
              <li>On <strong>Parameters</strong>, map fields with <code>{'{{name}}'}</code> tokens. Keep <code>{'{{fbc}}'}</code>, <code>{'{{fbp}}'}</code>, and <code>{'{{external_id}}'}</code>.</li>
              <li>Opening the template checks the JSON. It must parse and render a sample purchase Meta can accept. On <strong>Test</strong>, preview that JSON (optional: send with a Test Events code).</li>
              <li>Click <strong>Set as default</strong> when this should be the only shape sent to Meta.</li>
            </ol>
            <p className="uni-num--muted" style={{ margin: 0 }}>
              Only one template is the default. Empty tokens are omitted; PII is hashed automatically.
              {' '}
              <a href="https://developers.facebook.com/documentation/ads-commerce/conversions-api/parameters" target="_blank" rel="noreferrer">Meta parameter docs</a>
            </p>
          </div>

          {creating && (
            <div className="sb-tpl-create">
              <h3>New template</h3>
              <label className="sb-field">
                Name
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. My purchase payload"
                />
              </label>
              <label className="sb-field">
                Start from
                <select value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
                  {ordered.map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.name}
                      {row.is_system ? ' (Meta template)' : ''}
                      {row.is_active ? ' — current default' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <div className="sb-mw-head__actions">
                <UniActionButton hero primary onClick={createNew}>Create template</UniActionButton>
                <button type="button" className="ty-btn ty-btn--secondary" onClick={() => setCreating(false)}>Cancel</button>
              </div>
            </div>
          )}

          <p className="uni-num--muted">
            {defaultRow
              ? <>Current default: <strong>{defaultRow.name}</strong>{defaultRow.is_system ? ' (Meta’s template)' : ''}.</>
              : 'No default yet — Meta’s template is used until you set one.'}
          </p>

          <UniTable
            rows={ordered}
            loading={loading}
            empty="No templates yet. Meta’s CAPI template is created the first time this page loads."
            rowKey="id"
            columns={[
              {
                key: 'name',
                label: 'Template',
                render: (row) => (
                  <button type="button" className="sb-tpl-link" onClick={() => openEditor(row)}>
                    {row.name}
                  </button>
                ),
              },
              {
                key: 'kind',
                label: 'Kind',
                render: (row) => kindLabel(row),
              },
              {
                key: 'is_active',
                label: 'Default',
                render: (row) => (
                  row.is_active
                    ? <UniStatusPill value="DEFAULT" palette="green" />
                    : (
                      <button type="button" className="ty-btn ty-btn--secondary" onClick={() => activate(row)}>
                        Set as default
                      </button>
                    )
                ),
              },
              {
                key: 'updated_at',
                label: 'Updated',
                render: (row) => formatWhen(row.updated_at),
              },
              {
                key: 'actions',
                label: '',
                render: (row) => (
                  <div className="sb-mw-head__actions">
                    <button type="button" className="ty-btn" onClick={() => openEditor(row)}>Open</button>
                    <button type="button" className="ty-btn ty-btn--secondary" onClick={() => duplicate(row)}>Duplicate</button>
                    {!row.is_system && (
                      <button type="button" className="ty-btn ty-btn--secondary" onClick={() => remove(row)}>Delete</button>
                    )}
                  </div>
                ),
              },
            ]}
          />
        </>
      )}

      {view === 'edit' && selected && (
        <div className="sb-mw-main">
          <div className="sb-mw-head">
            <div>
              <h2 style={{ margin: 0 }}>{selected.name}</h2>
              <p className="uni-num--muted" style={{ margin: '4px 0 0' }}>
                {selected.is_system
                  ? 'Meta’s read-only template. Duplicate it to change the JSON. Set as default to send this shape.'
                  : 'Custom template. Save, then Set as default to send this shape only.'}
              </p>
            </div>
            <div className="sb-mw-head__actions">
              {selected.is_active ? <UniStatusPill value="DEFAULT" palette="green" /> : (
                <button
                  type="button"
                  className="ty-btn"
                  disabled={check && check.ok === false}
                  onClick={() => activate(selected)}
                >
                  Set as default
                </button>
              )}
              <button type="button" className="ty-btn ty-btn--secondary" onClick={() => duplicate(selected)}>Duplicate</button>
              {!selected.is_system && (
                <button type="button" className="ty-btn ty-btn--secondary" onClick={() => remove(selected)}>Delete</button>
              )}
            </div>
          </div>

          <TemplateCheck check={check} busy={checkBusy} />

          <ol className="sb-mw-steps">
            <li><strong>How Meta accepts JSON</strong> — server event, hashed user_data, custom_data.</li>
            <li><strong>Fit your parameters</strong> — <code>{'{{name}}'}</code> tokens filled from the order / visitor.</li>
            <li><strong>Test</strong> — preview the hashed body, then optionally send with a Test Events code.</li>
          </ol>

          <div className="sb-mw-tabs" role="tablist">
            {[
              { id: 'meta', label: '1. How Meta accepts JSON' },
              { id: 'params', label: '2. Parameters {{ }}' },
              { id: 'test', label: '3. Test' },
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={tab === item.id}
                className={`sb-mw-tab${tab === item.id ? ' sb-mw-tab--on' : ''}`}
                onClick={() => setTab(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>

          {tab === 'meta' && (
            <div>
              <UniSection title="Parameter families" />
              <p className="uni-num--muted">
                Website events need action_source, event_source_url, and client_user_agent.
                Hashed PII is SHA-256 of the normalized lowercase value. Do not hash fbc, fbp, IP, or user agent.
              </p>
              {families.map((family) => (
                <details key={family.id} className="sb-mw-family" open={family.id === 'user_data' || family.id === 'server_event'}>
                  <summary>
                    {family.title}
                    {family.docs_url ? (
                      <a href={family.docs_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}> docs</a>
                    ) : null}
                  </summary>
                  <p>{family.note}</p>
                  <table className="sb-mw-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Token</th>
                        <th>Hash</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(family.fields || []).map((field) => (
                        <tr key={field.key}>
                          <td>
                            <code>{field.key}</code>
                            {field.required ? ' *' : ''}
                          </td>
                          <td><code>{field.placeholder}</code></td>
                          <td>{field.hash === 'none' || field.hash === 'never' ? 'do not hash' : field.hash}</td>
                          <td>{field.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              ))}
              <UniSection title="Annotated sample (comments are stripped before send)" />
              <pre className="uni-json">{prettyJson(catalog && catalog.annotated_sample)}</pre>
              <UniSection title="Attribution-first sample" />
              <p className="uni-num--muted">fbc ties the purchase to the ad click. fbp + external_id identify the user inside Meta.</p>
              <pre className="uni-json">{prettyJson(catalog && catalog.attribution_sample)}</pre>
            </div>
          )}

          {tab === 'params' && (
            <div>
              <label className="sb-field">
                Name
                <input value={name} onChange={(e) => setName(e.target.value)} disabled={selected.is_system} />
              </label>
              <label className="sb-field">
                Description
                <input value={description} onChange={(e) => setDescription(e.target.value)} disabled={selected.is_system} />
              </label>
              <p className="uni-num--muted">Click a token to insert it at the cursor. Keep {`{{fbc}}`}, {`{{fbp}}`} and {`{{external_id}}`}.</p>
              <div className="sb-mw-chips">
                {placeholders.map((field) => (
                  <button
                    key={`${field.family}-${field.key}`}
                    type="button"
                    className={`sb-mw-chip${field.required ? ' sb-mw-chip--req' : ''}`}
                    onClick={() => insertToken(field.key)}
                    title={field.description}
                    disabled={selected.is_system}
                  >
                    {`{{${field.key}}}`}
                  </button>
                ))}
              </div>
              <textarea
                ref={bodyRef}
                className={`sb-mw-editor${check && check.ok === false ? ' sb-mw-editor--bad' : ''}`}
                rows={22}
                value={bodyText}
                onChange={(e) => {
                  setBodyText(e.target.value);
                  setEditorFor(String(selectedId));
                }}
                disabled={selected.is_system}
                spellCheck={false}
              />
              {!selected.is_system && (
                <>
                  <button
                    type="button"
                    className="ty-btn sb-mw-save"
                    disabled={saving || Boolean(jsonProblem) || (check && check.ok === false)}
                    onClick={save}
                  >
                    {saving ? 'Saving…' : 'Save template'}
                  </button>
                  {jsonProblem ? (
                    <p className="sb-connect-error">Save stays off until the JSON is valid and the check passes.</p>
                  ) : null}
                </>
              )}
              {selected.is_system && (
                <p className="uni-num--muted">Duplicate this Meta template to edit a copy. The default template is the only JSON posted to Meta.</p>
              )}
            </div>
          )}

          {tab === 'test' && (
            <div>
              <p className="uni-num--muted">
                Fill values for the placeholders. Preview hashes PII the way Meta expects. Sending uses Events Manager Test Events and does not edit the pixel.
              </p>
              <div className="sb-mw-testgrid">
                {Object.keys(testFields).sort().map((key) => (
                  <label key={key} className="sb-field">
                    {key}
                    <input
                      value={typeof testFields[key] === 'object' ? prettyJson(testFields[key]) : (testFields[key] ?? '')}
                      onChange={(e) => {
                        const raw = e.target.value;
                        let value = raw;
                        if (raw.startsWith('{') || raw.startsWith('[')) {
                          try { value = JSON.parse(raw); } catch (err) { value = raw; }
                        }
                        setTestFields((prev) => ({ ...prev, [key]: value }));
                      }}
                    />
                  </label>
                ))}
              </div>
              <div className="sb-mw-testbar">
                <label className="sb-field">
                  Recent incoming event
                  <select value={incomingId} onChange={(e) => setIncomingId(e.target.value)}>
                    <option value="">— sample values above —</option>
                    {(incoming || []).map((row) => (
                      <option key={row.id} value={row.id}>{(row.source || 'in')} {String(row.id).slice(0, 8)}</option>
                    ))}
                  </select>
                </label>
                <button type="button" className="ty-btn" disabled={previewBusy} onClick={runPreview}>
                  {previewBusy ? 'Rendering…' : 'Preview rendered JSON'}
                </button>
              </div>
              {preview && (
                <>
                  <UniSection title="Rendered event (what Meta receives in data[])" />
                  <pre className="uni-json">{prettyJson(preview.event || preview.envelope)}</pre>
                </>
              )}
              <UniSection title="Send as Meta test" />
              <div className="sb-mw-testbar">
                <label className="sb-field">
                  Pixel
                  <select value={pixel} onChange={(e) => setPixel(e.target.value)}>
                    <option value="">Choose pixel</option>
                    {(pixels || []).map((row) => (
                      <option key={row.id} value={row.id}>{row.name}</option>
                    ))}
                  </select>
                </label>
                <label className="sb-field">
                  Test event code
                  <input value={testCode} onChange={(e) => setTestCode(e.target.value)} placeholder="TEST12345" />
                </label>
                <button type="button" className="ty-btn" disabled={sendBusy || !pixel || !testCode} onClick={sendTest}>
                  {sendBusy ? 'Sending…' : 'Send test to Meta'}
                </button>
              </div>
              {sendOut && (
                <div className={sendOut.ok ? 'ty-ok' : 'ty-error'} style={{ marginTop: 12 }}>
                  {sendOut.hint || (sendOut.ok ? 'Test reached Meta.' : 'Test did not reach Meta.')}
                  {sendOut.ok ? (
                    <div>
                      Then open <Link to="/signalbridge/payloads">Meta payloads</Link> to inspect the stored JSON.
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </UniPageShell>
  );
}

export const SignalBridgeMiddleware = SignalBridgeTemplates;
